import { onBeforeUnmount, onMounted, ref } from 'vue'
import { palette } from '@/theme'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/**
 * The hero's drifting node network.
 *
 * Four fixes, in order of how much main-thread time they returned:
 *
 * 1. **It never stopped.** The rAF loop kept running after the hero scrolled
 *    away, animating a canvas nobody could see for the entire session. An
 *    IntersectionObserver now starts and stops it with visibility.
 * 2. **Link search was O(n²).** 68 nodes meant 2,278 distance checks every
 *    frame. Nodes are now sorted by x and scanned forward only while the x-gap
 *    is under the link distance, which skips the vast majority of pairs.
 * 3. **Node count ignored the device.** It now scales down further on small
 *    screens and on machines reporting few cores.
 * 4. **devicePixelRatio was capped at 2**, so a 3x phone still rasterised four
 *    times the pixels of a 1.5x cap that looks the same at this opacity.
 *
 * The visual is unchanged: same colours, same drift, same link falloff.
 */
export function useNetworkCanvas(options = {}) {
  const { pointerDrift = true } = options

  const canvas = ref(null)
  const reduced = usePrefersReducedMotion()

  let ctx = null
  let frame = 0
  let observer = null
  let running = false
  let width = 0
  let height = 0
  let linkDistance = 150
  let nodes = []

  const pointer = { x: 0, y: 0 }

  const nodeCount = () => {
    const cores = navigator.hardwareConcurrency || 4
    if (width < 700) return cores <= 4 ? 16 : 22
    if (width < 1200) return cores <= 4 ? 26 : 36
    return cores <= 4 ? 34 : 48
  }

  const build = () => {
    linkDistance = width < 700 ? 110 : 150
    nodes = Array.from({ length: nodeCount() }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.18,
      vy: (Math.random() - 0.5) * 0.18,
      r: Math.random() * 1.6 + 1,
      depth: Math.random() * 0.6 + 0.4
    }))
  }

  const resize = () => {
    const node = canvas.value
    if (!node || !ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
    const rect = node.getBoundingClientRect()
    width = rect.width
    height = rect.height
    node.width = Math.round(width * dpr)
    node.height = Math.round(height * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    build()
  }

  let resizeFrame = 0
  const onResize = () => {
    cancelAnimationFrame(resizeFrame)
    resizeFrame = requestAnimationFrame(resize)
  }

  const onPointerMove = (event) => {
    pointer.x = event.clientX
    pointer.y = event.clientY
  }

  const tick = () => {
    if (!running || !ctx) return
    frame = requestAnimationFrame(tick)

    ctx.clearRect(0, 0, width, height)

    const driftX = pointerDrift ? (pointer.x / Math.max(width, 1) - 0.5) * 14 : 0
    const driftY = pointerDrift ? (pointer.y / Math.max(height, 1) - 0.5) * 10 : 0

    for (const node of nodes) {
      node.x += node.vx
      node.y += node.vy
      if (node.x < 0 || node.x > width) node.vx *= -1
      if (node.y < 0 || node.y > height) node.vy *= -1
    }

    // Sweep and prune: sorted by x, the inner loop breaks as soon as the
    // horizontal gap alone exceeds the link distance.
    nodes.sort((a, b) => a.x - b.x)

    ctx.strokeStyle = palette.brand
    ctx.lineWidth = 1
    for (let i = 0; i < nodes.length; i += 1) {
      const a = nodes[i]
      for (let j = i + 1; j < nodes.length; j += 1) {
        const b = nodes[j]
        const dx = b.x - a.x
        if (dx > linkDistance) break

        const dy = a.y - b.y
        const distance = Math.hypot(dx, dy)
        if (distance > linkDistance) continue

        ctx.globalAlpha = (1 - distance / linkDistance) * 0.16
        ctx.beginPath()
        ctx.moveTo(a.x + driftX * a.depth, a.y + driftY * a.depth)
        ctx.lineTo(b.x + driftX * b.depth, b.y + driftY * b.depth)
        ctx.stroke()
      }
    }

    for (const node of nodes) {
      ctx.globalAlpha = 0.32 * node.depth + 0.12
      ctx.fillStyle = node.depth > 0.8 ? palette.info : palette.brand
      ctx.beginPath()
      ctx.arc(node.x + driftX * node.depth, node.y + driftY * node.depth, node.r, 0, Math.PI * 2)
      ctx.fill()
    }

    ctx.globalAlpha = 1
  }

  const start = () => {
    if (running || reduced.value || !ctx) return
    running = true
    if (pointerDrift) window.addEventListener('pointermove', onPointerMove, { passive: true })
    frame = requestAnimationFrame(tick)
  }

  const stop = () => {
    running = false
    cancelAnimationFrame(frame)
    window.removeEventListener('pointermove', onPointerMove)
  }

  const onVisibility = () => {
    if (document.hidden) stop()
    else if (observer) start()
  }

  onMounted(() => {
    if (reduced.value || !canvas.value) return

    ctx = canvas.value.getContext('2d', { alpha: true })
    if (!ctx) return

    resize()
    window.addEventListener('resize', onResize, { passive: true })
    document.addEventListener('visibilitychange', onVisibility)

    // Only animate while the hero is actually on screen.
    observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()), {
      threshold: 0
    })
    observer.observe(canvas.value)
  })

  onBeforeUnmount(() => {
    stop()
    cancelAnimationFrame(resizeFrame)
    observer?.disconnect()
    observer = null
    window.removeEventListener('resize', onResize)
    document.removeEventListener('visibilitychange', onVisibility)
    ctx = null
    nodes = []
  })

  return { canvas }
}
